import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

const AudioManager = () => {
  // =====================================================
  // FORM STATES
  // =====================================================

  const [title, setTitle] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [audioFileName, setAudioFileName] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [category, setCategory] = useState("");

  // =====================================================
  // DATA STATES
  // =====================================================

  const [categories, setCategories] = useState([]);
  const [audios, setAudios] = useState([]);

  // =====================================================
  // LOADING STATES
  // =====================================================

  const [loading, setLoading] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] =
    useState(false);

  // =====================================================
  // EDIT
  // =====================================================

  const [editingId, setEditingId] = useState(null);

  // =====================================================
  // SEARCH
  // =====================================================

  const [searchTerm, setSearchTerm] = useState("");

  // =====================================================
  // PAGINATION
  // =====================================================

  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 20;

  // =====================================================
  // HOVER
  // =====================================================

  const [hoveredButton, setHoveredButton] = useState(null);

  // =====================================================
  // CLOUDINARY SETTINGS
  // =====================================================

  const CLOUDINARY_CLOUD_NAME = "odpkecys";

  const CLOUDINARY_UPLOAD_PRESET =
    "vawaiyaexpress_audio";

  // =====================================================
  // IMGBB SETTINGS
  // =====================================================

  const IMGBB_API_KEY =
    "874aba0b22e62f51c225a6c83f3f6da5";

  // =====================================================
  // LOAD CATEGORIES
  // =====================================================

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, "categories")
        );

        const data = snapshot.docs
          .map((item) => ({
            id: item.id,
            ...item.data(),
          }))
          .sort(
            (a, b) =>
              Number(a.order || 0) -
              Number(b.order || 0)
          );

        setCategories(data);

        if (data.length > 0) {
          setCategory(data[0].name);
        }
      } catch (error) {
        console.error(
          "Category load error:",
          error
        );
      }
    };

    fetchCategories();
  }, []);

  // =====================================================
  // LOAD AUDIOS
  // =====================================================

  const fetchAudios = async () => {
    try {
      const q = query(
        collection(db, "audios"),
        orderBy("timestamp", "desc")
      );

      const snapshot = await getDocs(q);

      const data = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setAudios(data);
    } catch (error) {
      console.error(
        "Audio load error:",
        error
      );
    }
  };

  useEffect(() => {
    fetchAudios();
  }, []);

  // =====================================================
  // AUDIO FILE UPLOAD - CLOUDINARY
  // =====================================================

const handleAudioUpload = async (e) => {
  const file = e.target.files[0];

  if (!file) return;

  const allowedExtensions = [
    ".mp3",
    ".mpeg",
    ".mp4",
    ".m4a",
    ".aac",
    ".wav",
    ".ogg",
  ];

  const fileName = file.name.toLowerCase();

  const isAllowed = allowedExtensions.some(
    (ext) => fileName.endsWith(ext)
  );

  if (!isAllowed) {
    alert(
      "এই ফাইলটি সাপোর্ট করে না। MP3, MPEG, MP4, M4A, AAC, WAV বা OGG ফাইল দিন।"
    );

    e.target.value = "";
    return;
  }

  // 100 MB maximum
  const maxSize = 100 * 1024 * 1024;

  if (file.size > maxSize) {
    alert(
      "অডিও ফাইলের সর্বোচ্চ সাইজ 100 MB হতে পারে।"
    );

    e.target.value = "";
    return;
  }

  setUploadingAudio(true);
  setAudioFileName(file.name);

  try {
    const formData = new FormData();

    formData.append("file", file);

    formData.append(
      "upload_preset",
      CLOUDINARY_UPLOAD_PRESET
    );

    const uploadUrl =
      `https://api.cloudinary.com/v1_1/` +
      `${CLOUDINARY_CLOUD_NAME}/video/upload`;

    const response = await fetch(
      uploadUrl,
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();

    if (!response.ok || !data.secure_url) {
      console.error(
        "Cloudinary response:",
        data
      );

      throw new Error(
        data.error?.message ||
          "Audio upload failed"
      );
    }

    setAudioUrl(data.secure_url);

    alert(
      "অডিও সফলভাবে আপলোড হয়েছে!"
    );
  } catch (error) {
    console.error(
      "Audio upload error:",
      error
    );

    setAudioUrl("");
    setAudioFileName("");

    alert(
      "অডিও আপলোড করতে সমস্যা হয়েছে!"
    );
  } finally {
    setUploadingAudio(false);
    e.target.value = "";
  }
};

  // =====================================================
  // THUMBNAIL UPLOAD - IMGBB
  // =====================================================

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert(
        "দয়া করে একটি image file নির্বাচন করুন!"
      );

      e.target.value = "";
      return;
    }

    setUploadingThumbnail(true);

    try {
      const formData = new FormData();

      formData.append("image", file);

      const response = await fetch(
        `https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error(
          "ImgBB response:",
          data
        );

        throw new Error(
          "Thumbnail upload failed"
        );
      }

      setThumbnail(data.data.url);

      alert(
        "Thumbnail সফলভাবে আপলোড হয়েছে!"
      );
    } catch (error) {
      console.error(
        "Thumbnail upload error:",
        error
      );

      alert(
        "Thumbnail আপলোড করতে সমস্যা হয়েছে!"
      );
    } finally {
      setUploadingThumbnail(false);
      e.target.value = "";
    }
  };

  // =====================================================
  // ADD / UPDATE AUDIO
  // =====================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("অডিওর শিরোনাম দিন!");
      return;
    }

    if (!audioUrl) {
      alert("অডিও ফাইল নির্বাচন করুন!");
      return;
    }

    if (!thumbnail) {
      alert("অডিওর Thumbnail দিন!");
      return;
    }

    if (!category) {
      alert("Category নির্বাচন করুন!");
      return;
    }

    setLoading(true);

    try {
      // =================================================
      // UPDATE
      // =================================================

      if (editingId) {
        await updateDoc(
          doc(db, "audios", editingId),
          {
            title: title.trim(),
            audioUrl: audioUrl,
            thumbnail: thumbnail,
            category: category,
          }
        );

        alert(
          "অডিও সফলভাবে আপডেট হয়েছে!"
        );
      }

      // =================================================
      // ADD
      // =================================================

      else {
        await addDoc(
          collection(db, "audios"),
          {
            title: title.trim(),
            audioUrl: audioUrl,
            thumbnail: thumbnail,
            category: category,
            status: "active",
            timestamp: serverTimestamp(),
          }
        );

        alert(
          "অডিও সফলভাবে প্রকাশিত হয়েছে!"
        );
      }

      resetForm();

      await fetchAudios();
    } catch (error) {
      console.error(
        "Audio save error:",
        error
      );

      alert(
        editingId
          ? "অডিও আপডেট করতে সমস্যা হয়েছে!"
          : "অডিও প্রকাশ করতে সমস্যা হয়েছে!"
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setTitle("");
    setAudioUrl("");
    setAudioFileName("");
    setThumbnail("");
    setEditingId(null);

    if (categories.length > 0) {
      setCategory(categories[0].name);
    } else {
      setCategory("");
    }
  };

  // =====================================================
  // EDIT AUDIO
  // =====================================================

  const handleEdit = (audio) => {
    setEditingId(audio.id);

    setTitle(audio.title || "");
    setAudioUrl(audio.audioUrl || "");
    setAudioFileName(
      audio.fileName || "বর্তমান অডিও ফাইল"
    );
    setThumbnail(audio.thumbnail || "");
    setCategory(audio.category || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =====================================================
  // DELETE AUDIO
  // =====================================================

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "আপনি কি এই অডিওটি ডিলিট করতে চান?"
    );

    if (!confirmed) return;

    try {
      await deleteDoc(
        doc(db, "audios", id)
      );

      setAudios((prev) =>
        prev.filter(
          (audio) => audio.id !== id
        )
      );

      alert("অডিও ডিলিট হয়েছে!");
    } catch (error) {
      console.error(
        "Audio delete error:",
        error
      );

      alert(
        "অডিও ডিলিট করতে সমস্যা হয়েছে!"
      );
    }
  };

  // =====================================================
  // SEARCH FILTER
  // =====================================================

  const filteredAudios = audios.filter(
    (audio) => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      if (!search) {
        return true;
      }

      return (
        (audio.title || "")
          .toLowerCase()
          .includes(search) ||
        (audio.category || "")
          .toLowerCase()
          .includes(search)
      );
    }
  );

  // =====================================================
  // PAGINATION
  // =====================================================

  const totalPages = Math.ceil(
    filteredAudios.length /
      ITEMS_PER_PAGE
  );

  const startIndex =
    (currentPage - 1) *
    ITEMS_PER_PAGE;

  const currentAudios =
    filteredAudios.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );

  // =====================================================
  // SEARCH CHANGE
  // =====================================================

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return "—";
    }

    let date;

    if (
      timestamp &&
      typeof timestamp.seconds ===
        "number"
    ) {
      date = new Date(
        timestamp.seconds * 1000
      );
    } else {
      date = new Date(timestamp);
    }

    if (
      Number.isNaN(date.getTime())
    ) {
      return "—";
    }

    return date.toLocaleDateString(
      "bn-BD",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }
    );
  };

  // =====================================================
  // FILE SIZE
  // =====================================================

  const formatFileName = (name) => {
    if (!name) return "";

    if (name.length <= 40) {
      return name;
    }

    return (
      name.substring(0, 37) +
      "..."
    );
  };

  // =====================================================
  // ACTION BUTTON STYLE
  // =====================================================

  const actionButtonStyle = (
    type,
    hovered
  ) => ({
    padding: "7px 12px",
    border: "none",
    borderRadius: "4px",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "bold",
    background:
      type === "edit"
        ? hovered
          ? "#2452b8"
          : "#4169E1"
        : hovered
        ? "#8b0000"
        : "#666",
    transition:
      "all 0.2s ease",
    transform: hovered
      ? "translateY(-1px)"
      : "translateY(0)",
  });

  // =====================================================
  // RETURN
  // =====================================================

  return (
    <div
      style={{
        padding: "10px",
      }}
    >
      {/* =================================================
          MAIN HEADING
      ================================================= */}

      <h3
        style={{
          margin: 0,
          marginBottom: "20px",
          borderBottom:
            "2px solid #d2202a",
          paddingBottom: "10px",
          fontSize: "23px",
          color: "#222",
        }}
      >
        🎧 অডিও ম্যানেজমেন্ট
      </h3>

      {/* =================================================
          ADD / EDIT AUDIO
      ================================================= */}

      <div
        style={{
          border:
            "1px solid #ddd",
          borderRadius: "6px",
          padding: "18px",
          marginBottom: "30px",
          background: "#fff",
        }}
      >
        <h4
          style={{
            margin: "0 0 15px",
            borderLeft:
              "5px solid #d2202a",
            paddingLeft: "10px",
            fontSize: "18px",
          }}
        >
          {editingId
            ? "অডিও সম্পাদনা"
            : "নতুন অডিও প্রকাশ"}
        </h4>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "grid",
            gridTemplateColumns:
              "1fr 1fr",
            gap: "15px",
          }}
        >
          {/* TITLE */}

          <input
            type="text"
            placeholder="অডিওর শিরোনাম"
            value={title}
            onChange={(e) =>
              setTitle(e.target.value)
            }
            style={{
              padding: "12px",
              fontSize: "16px",
              border:
                "1px solid #ccc",
              borderRadius: "4px",
              boxSizing:
                "border-box",
              width: "100%",
            }}
          />

          {/* AUDIO FILE */}

          <label
            style={{
              padding: "11px 12px",
              border:
                "1px dashed #bbb",
              borderRadius: "4px",
              cursor:
                uploadingAudio
                  ? "wait"
                  : "pointer",
              background:
                uploadingAudio
                  ? "#f3f3f3"
                  : "#fafafa",
              color: "#555",
              fontWeight: "600",
              textAlign: "center",
              minHeight: "20px",
            }}
          >
            {uploadingAudio
              ? "🎵 অডিও আপলোড হচ্ছে..."
              : audioFileName
              ? `✓ ${formatFileName(
                  audioFileName
                )}`
              : editingId && audioUrl
              ? "✓ বর্তমান অডিও আছে — নতুনটি দিতে চাইলে নির্বাচন করুন"
              : "🎵 অডিও ফাইল নির্বাচন করুন"}

           <input
  type="file"
  accept=".mp3,.mpeg,.mp4,.m4a,.aac,.wav,.ogg,audio/*,video/mp4"
  onChange={handleAudioUpload}
  disabled={uploadingAudio}
  style={{
    display: "none",
  }}
/>
          </label>

          {/* CATEGORY */}

          <select
            value={category}
            onChange={(e) =>
              setCategory(e.target.value)
            }
            style={{
              padding: "12px",
              fontSize: "16px",
              border:
                "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
              width: "100%",
              boxSizing:
                "border-box",
            }}
          >
            {categories.length === 0 ? (
              <option value="">
                কোনো category নেই
              </option>
            ) : (
              categories.map(
                (cat) => (
                  <option
                    key={cat.id}
                    value={cat.name}
                  >
                    {cat.name}
                  </option>
                )
              )
            )}
          </select>

          {/* THUMBNAIL */}

          <label
            style={{
              padding: "11px 12px",
              border:
                "1px dashed #bbb",
              borderRadius: "4px",
              cursor:
                uploadingThumbnail
                  ? "wait"
                  : "pointer",
              background:
                uploadingThumbnail
                  ? "#f3f3f3"
                  : "#fafafa",
              color: "#555",
              fontWeight: "600",
              textAlign: "center",
            }}
          >
            {uploadingThumbnail
              ? "🖼️ Thumbnail আপলোড হচ্ছে..."
              : thumbnail
              ? "✓ Thumbnail নির্বাচিত"
              : "🖼️ Thumbnail নির্বাচন করুন"}

            <input
              type="file"
              accept="image/*"
              onChange={
                handleThumbnailUpload
              }
              disabled={
                uploadingThumbnail
              }
              style={{
                display: "none",
              }}
            />
          </label>

          {/* AUDIO STATUS */}

          {audioUrl && (
            <div
              style={{
                gridColumn:
                  "1 / -1",
                padding: "10px 12px",
                background:
                  "#f5f9ff",
                border:
                  "1px solid #dce8ff",
                borderRadius: "4px",
                color: "#4169E1",
                fontSize: "14px",
              }}
            >
              🎵 অডিও প্রস্তুত হয়েছে।
              {audioFileName && (
                <span
                  style={{
                    marginLeft:
                      "8px",
                    color: "#555",
                  }}
                >
                  {formatFileName(
                    audioFileName
                  )}
                </span>
              )}
            </div>
          )}

          {/* THUMBNAIL PREVIEW */}

          {thumbnail && (
            <div
              style={{
                gridColumn:
                  "1 / -1",
                display: "flex",
                alignItems:
                  "center",
                gap: "15px",
                padding: "10px",
                border:
                  "1px solid #eee",
                borderRadius: "5px",
                background:
                  "#fafafa",
              }}
            >
              <img
                src={thumbnail}
                alt="Audio thumbnail"
                style={{
                  width: "140px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  display: "block",
                }}
              />

              <span
                style={{
                  color: "#555",
                  fontSize: "14px",
                }}
              >
                Thumbnail preview
              </span>
            </div>
          )}

          {/* PUBLISH / UPDATE */}

          <button
            type="submit"
            disabled={
              loading ||
              uploadingAudio ||
              uploadingThumbnail
            }
            onMouseEnter={() =>
              setHoveredButton(
                "submit"
              )
            }
            onMouseLeave={() =>
              setHoveredButton(null)
            }
            style={{
              gridColumn:
                "1 / -1",
              padding: "13px",
              background:
                loading ||
                uploadingAudio ||
                uploadingThumbnail
                  ? "#999"
                  : hoveredButton ===
                    "submit"
                  ? "#8b0000"
                  : "#666",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              fontSize: "17px",
              fontWeight: "bold",
              cursor:
                loading ||
                uploadingAudio ||
                uploadingThumbnail
                  ? "not-allowed"
                  : "pointer",
              transition:
                "all 0.2s ease",
            }}
          >
            {loading
              ? editingId
                ? "আপডেট হচ্ছে..."
                : "প্রকাশ হচ্ছে..."
              : editingId
              ? "অডিও আপডেট করুন"
              : "অডিও প্রকাশ করুন"}
          </button>

          {/* CANCEL */}

          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              onMouseEnter={() =>
                setHoveredButton(
                  "cancel"
                )
              }
              onMouseLeave={() =>
                setHoveredButton(null)
              }
              style={{
                gridColumn:
                  "1 / -1",
                padding: "11px",
                background:
                  hoveredButton ===
                  "cancel"
                    ? "#8b0000"
                    : "#666",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                transition:
                  "all 0.2s ease",
              }}
            >
              বাতিল
            </button>
          )}
        </form>
      </div>

      {/* =================================================
          PUBLISHED AUDIO LIST HEADER
      ================================================= */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: "15px",
          marginBottom: "15px",
          borderBottom:
            "1px solid #eee",
          paddingBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        <h4
          style={{
            margin: 0,
            borderLeft:
              "5px solid #d2202a",
            paddingLeft: "10px",
            fontSize: "18px",
          }}
        >
          প্রকাশিত অডিও
        </h4>

        {/* SEARCH */}

        <input
          type="text"
          placeholder="🔍 অডিও খুঁজুন..."
          value={searchTerm}
          onChange={
            handleSearchChange
          }
          style={{
            width: "280px",
            maxWidth: "100%",
            padding: "9px 12px",
            border:
              "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "14px",
            outline: "none",
            boxSizing:
              "border-box",
          }}
        />
      </div>

      {/* =================================================
          AUDIO LIST
      ================================================= */}

      {currentAudios.length === 0 ? (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "#777",
            border:
              "1px solid #eee",
            borderRadius: "5px",
          }}
        >
          {searchTerm
            ? "কোনো অডিও পাওয়া যায়নি।"
            : "কোনো প্রকাশিত অডিও নেই।"}
        </div>
      ) : (
        <div
          style={{
            overflowX: "auto",
            border:
              "1px solid #ddd",
            borderRadius: "5px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse:
                "collapse",
              minWidth: "900px",
            }}
          >
            <thead>
              <tr
                style={{
                  background:
                    "#f5f5f5",
                }}
              >
                <th style={thStyle}>
                  SL
                </th>

                <th style={thStyle}>
                  প্রকাশের তারিখ
                </th>

                <th style={thStyle}>
                  শিরোনাম
                </th>

                <th style={thStyle}>
                  Thumbnail
                </th>

                <th style={thStyle}>
                  Category
                </th>

                <th style={thStyle}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {currentAudios.map(
                (audio, index) => (
                  <tr
                    key={audio.id}
                    style={{
                      borderTop:
                        "1px solid #eee",
                    }}
                  >
                    {/* SL */}

                    <td
                      style={tdStyle}
                    >
                      {startIndex +
                        index +
                        1}
                    </td>

                    {/* DATE */}

                    <td
                      style={tdStyle}
                    >
                      {formatDate(
                        audio.timestamp
                      )}
                    </td>

                    {/* TITLE */}

                    <td
                      style={{
                        ...tdStyle,
                        fontWeight:
                          "600",
                        maxWidth:
                          "300px",
                      }}
                    >
                      {audio.title}
                    </td>

                    {/* THUMBNAIL */}

                    <td
                      style={tdStyle}
                    >
                      {audio.thumbnail ? (
                        <img
                          src={
                            audio.thumbnail
                          }
                          alt={
                            audio.title
                          }
                          style={{
                            width:
                              "100px",
                            height:
                              "58px",
                            objectFit:
                              "cover",
                            borderRadius:
                              "4px",
                            display:
                              "block",
                          }}
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* CATEGORY */}

                    <td
                      style={{
                        ...tdStyle,
                        color:
                          "#d2202a",
                        fontWeight:
                          "600",
                      }}
                    >
                      {audio.category ||
                        "—"}
                    </td>

                    {/* ACTION */}

                    <td
                      style={{
                        ...tdStyle,
                        whiteSpace:
                          "nowrap",
                      }}
                    >
                      {/* EDIT */}

                      <button
                        type="button"
                        onClick={() =>
                          handleEdit(
                            audio
                          )
                        }
                        onMouseEnter={() =>
                          setHoveredButton(
                            `edit-${audio.id}`
                          )
                        }
                        onMouseLeave={() =>
                          setHoveredButton(
                            null
                          )
                        }
                        style={actionButtonStyle(
                          "edit",
                          hoveredButton ===
                            `edit-${audio.id}`
                        )}
                      >
                        ✏️ Edit
                      </button>

                      {/* DELETE */}

                      <button
                        type="button"
                        onClick={() =>
                          handleDelete(
                            audio.id
                          )
                        }
                        onMouseEnter={() =>
                          setHoveredButton(
                            `delete-${audio.id}`
                          )
                        }
                        onMouseLeave={() =>
                          setHoveredButton(
                            null
                          )
                        }
                        style={{
                          ...actionButtonStyle(
                            "delete",
                            hoveredButton ===
                              `delete-${audio.id}`
                          ),
                          marginLeft:
                            "7px",
                        }}
                      >
                        🗑️ Delete
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* =================================================
          PAGINATION
      ================================================= */}

      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent:
              "center",
            alignItems: "center",
            gap: "6px",
            marginTop: "20px",
            flexWrap: "wrap",
          }}
        >
          {/* PREVIOUS */}

          <button
            type="button"
            disabled={
              currentPage === 1
            }
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  Math.max(
                    1,
                    prev - 1
                  )
              )
            }
            style={{
              ...pageButtonStyle,
              opacity:
                currentPage === 1
                  ? 0.5
                  : 1,
              cursor:
                currentPage === 1
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            ‹
          </button>

          {/* PAGE NUMBERS */}

          {Array.from(
            {
              length: totalPages,
            },
            (_, index) =>
              index + 1
          ).map((page) => (
            <button
              type="button"
              key={page}
              onClick={() =>
                setCurrentPage(
                  page
                )
              }
              style={{
                ...pageButtonStyle,
                background:
                  currentPage === page
                    ? "#d2202a"
                    : "#f1f1f1",
                color:
                  currentPage === page
                    ? "#fff"
                    : "#333",
              }}
            >
              {page}
            </button>
          ))}

          {/* NEXT */}

          <button
            type="button"
            disabled={
              currentPage ===
              totalPages
            }
            onClick={() =>
              setCurrentPage(
                (prev) =>
                  Math.min(
                    totalPages,
                    prev + 1
                  )
              )
            }
            style={{
              ...pageButtonStyle,
              opacity:
                currentPage ===
                totalPages
                  ? 0.5
                  : 1,
              cursor:
                currentPage ===
                totalPages
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* =================================================
          RESPONSIVE
      ================================================= */}

      <style>{`
        @media (max-width: 700px) {
          form {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

// =====================================================
// TABLE STYLES
// =====================================================

const thStyle = {
  padding: "12px 10px",
  textAlign: "left",
  fontSize: "14px",
  fontWeight: "700",
  color: "#444",
  borderBottom:
    "1px solid #ddd",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "11px 10px",
  fontSize: "14px",
  color: "#333",
  verticalAlign: "middle",
};

const pageButtonStyle = {
  minWidth: "34px",
  height: "34px",
  padding: "0 8px",
  border: "none",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "14px",
  cursor: "pointer",
  transition:
    "all 0.2s ease",
};

export default AudioManager;